/**
 * pdf.js holen — erst, wenn wirklich ein PDF gezeigt wird. (Ticket 2396,
 * 28.09.2026)
 *
 * Martin am iPhone, ein PDF-Link aus dem Chat: *„Das PDF passt nicht in die
 * Breite. Es lässt sich seitlich verschieben und wird nicht als Ganzes
 * angezeigt.“* Warum, steht in pdf-schau.ts. Kurz: den eingebetteten Rahmen
 * malt die WebView selbst, in Originalgröße. pdf.js zeichnet die Seiten
 * stattdessen hier, in der Breite, die die Ansicht hat.
 *
 * ⚠️ NICHT ins app.js gebündelt. pdf.js sind rund 1,5 MB (Bibliothek und
 *    Hilfsarbeiter); im Bündel läge das bei JEDEM Kaltstart mit auf dem
 *    Telefon, auch an Tagen ohne ein einziges PDF. Der Bau kopiert die
 *    Dateien nach public/pdfjs/ (scripts/pdfjs-kopieren.mts), geladen wird
 *    beim ersten PDF.
 * ⚠️ Die „legacy“-Fassung von pdf.js: Sie läuft auch in der WebView älterer
 *    iOS-Fassungen. Die moderne setzt die neuesten Browser voraus.
 */

/**
 * Wo der Bau pdf.js ablegt, und was er dorthin legt. Der Bau
 * (scripts/pdfjs-kopieren.mts) kopiert GENAU diese Dateien und Ordner —
 * ein Name steht nur hier. (Nachbesserung 28.09.2026: vorher standen die
 * Dateinamen hier und im Bau je einmal. Ein Tippfehler auf einer Seite liess
 * die Ansicht still auf den alten Rahmen zurückfallen.)
 */
export const PDFJS_ORT = "/pdfjs/";
export const PDFJS_DATEI = { bibliothek: "pdf.min.mjs", arbeiter: "pdf.worker.min.mjs" } as const;
/** Standardschriften (PDFs ohne eingebettete Helvetica & Co.) und wasm-Decoder (Scans). */
export const PDFJS_ORDNER = { schriften: "standard_fonts", wasm: "wasm" } as const;

/** Eine Seitengröße in der Einheit, die `scale` vorgibt (1 = PDF-Punkte). */
export interface PdfMass {
  width: number;
  height: number;
}

export interface PdfSeite {
  getViewport(o: { scale: number }): PdfMass;
  render(o: { canvas: HTMLCanvasElement; viewport: PdfMass }): { promise: Promise<void> };
  /** Der Text der Seite, für die Textebene (markieren, kopieren, VoiceOver). */
  streamTextContent(): ReadableStream;
  cleanup(): void;
}

export interface PdfDokument {
  numPages: number;
  getPage(nummer: number): Promise<PdfSeite>;
}

/**
 * Das Öffnen eines Dokuments. `destroy` gibt das Dokument UND seinen
 * Hilfsarbeiter frei — auch dann, wenn es noch gar nicht offen ist.
 */
export interface PdfLadeAuftrag {
  promise: Promise<PdfDokument>;
  destroy(): Promise<void>;
}

/** Die Textebene einer Seite: durchsichtiger Text genau über dem Bild. */
interface PdfTextEbene {
  render(): Promise<void>;
}

/** Der schmale Teil von pdf.js, den die Ansicht braucht. */
export interface PdfJs {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument(o: {
    data: Uint8Array;
    standardFontDataUrl?: string;
    wasmUrl?: string;
  }): PdfLadeAuftrag;
  TextLayer: new (o: {
    textContentSource: ReadableStream;
    container: HTMLElement;
    viewport: PdfMass;
  }) => PdfTextEbene;
}

/** Eine Adresse unter dem Ort von pdf.js, absolut zur laufenden Seite. */
function unterPdfJs(name: string): string {
  return new URL(`${PDFJS_ORT}${name}`, location.href).href;
}

/** Ein Dokument aus seinen Bytes öffnen — mit den Schriften und Decodern vom Bau. */
export function oeffneDokument(pdfjs: PdfJs, daten: Uint8Array): PdfLadeAuftrag {
  return pdfjs.getDocument({
    data: daten,
    standardFontDataUrl: unterPdfJs(`${PDFJS_ORDNER.schriften}/`),
    wasmUrl: unterPdfJs(`${PDFJS_ORDNER.wasm}/`),
  });
}

let geladen: Promise<PdfJs> | null = null;

/**
 * pdf.js, einmal je Sitzung geholt. Scheitert das Holen (kein Netz), wird es
 * beim nächsten PDF neu versucht — kein gemerkter Fehlschlag.
 */
export function ladePdfJs(): Promise<PdfJs> {
  if (geladen) return geladen;
  // Eine Adresse, die erst zur Laufzeit entsteht: esbuild lässt sie stehen,
  // statt pdf.js doch ins Bündel zu ziehen.
  const adresse = unterPdfJs(PDFJS_DATEI.bibliothek);
  geladen = (import(adresse) as Promise<PdfJs>).then(
    (pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = unterPdfJs(PDFJS_DATEI.arbeiter);
      return pdfjs;
    },
    (err: unknown) => {
      geladen = null;
      throw err;
    },
  );
  return geladen;
}
